import type { InternalPage } from '../lib/pages.ts'

/**
 * Every page in the internal webapp, in the order the rail shows them.
 *
 * This file belongs to the apps. It sits under `src/routes/` rather than in
 * `src/lib/` because every page ever added to this repo had to edit the list,
 * and a page should touch its own tree rather than the framework's. The shape
 * and the grouping logic stay in `src/lib/pages.ts`, which is framework.
 *
 * The `-` prefix is TanStack Router's ignore prefix, so this file adds no
 * route. Add an entry when you add a route under `src/routes/<app>/`, and
 * `-nav.test.ts` fails if you forget.
 *
 * **Every page carries a group, and the group matches the first segment of
 * the path.** A reader navigates `/payments/ach-returns` and looks for it
 * under Payments; a rail that listed it flat would answer a question the URL
 * had already answered differently. `-nav.test.ts` holds the two halves in
 * step, so a page added under a new app folder fails until it names a group.
 *
 * The rail in `__root.tsx` is the only reader. A signed-in employee sees
 * every entry that names no `audience`, and an entry that names one only when
 * `GET /viewer-access` puts them in it. They can then hide an entry in the
 * rail's customize mode (ADR-0014). Both are display choices, not access
 * control. A hidden page still opens from its link, and the handlers behind a
 * limited page are what refuse everybody else.
 */
export const INTERNAL_PAGES: readonly InternalPage[] = Object.freeze([
  {
    id: 'blytzcollect-analytics',
    path: '/blytzcollect/analytics',
    title: 'BlytzCollect analytics',
    group: 'BlytzCollect'
  },
  {
    id: 'ach-returns',
    path: '/payments/ach-returns',
    title: 'ACH returns',
    group: 'Payments'
  },
  {
    id: 'bin-mcc-fee-report',
    path: '/payments/bin-mcc-fee-report',
    title: 'BIN / MCC fee report',
    group: 'Payments'
  },
  {
    id: 'multicard-usage',
    path: '/payments/multicard-usage',
    title: 'Multicard usage',
    group: 'Payments'
  },
  {
    id: 'retry-analysis',
    path: '/reports/retry-analysis',
    title: 'Retry analysis',
    group: 'Reports'
  },
  {
    id: 'ai-texts',
    path: '/reports/ai-texts',
    title: 'AI texts',
    group: 'Reports'
  },
  {
    id: 'cs-tools-autopay-accounts',
    path: '/cs-tools/autopay-accounts',
    title: 'Autopay accounts',
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-merchant-notes',
    path: '/cs-tools/merchant-notes',
    title: 'Merchant notes',
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-registrations',
    path: '/cs-tools/registrations',
    title: 'Registrations',
    group: 'CS Tools'
  },
  {
    id: 'payment-report',
    path: '/cs-tools/payment-report',
    title: 'Payment report',
    group: 'CS Tools'
  },
  {
    id: 'message-report',
    path: '/cs-tools/message-report',
    title: 'Message report',
    group: 'CS Tools'
  },
  {
    id: 'undelivered',
    path: '/cs-tools/undelivered',
    title: 'Undelivered',
    group: 'CS Tools'
  },
  {
    id: 'opt-outs',
    path: '/cs-tools/opt-outs',
    title: 'Opt-outs',
    group: 'CS Tools'
  },
  {
    id: 'reconciliation',
    path: '/cs-tools/reconciliation',
    title: 'Reconciliation',
    group: 'CS Tools'
  },
  {
    id: 'merchant-settings',
    path: '/reports/merchant-settings',
    title: 'Merchant settings',
    group: 'Reports'
  },
  {
    id: 'report-chat',
    path: '/lab/report-chat',
    title: 'Report chat',
    group: 'Lab',
    audience: 'engineering'
  },
  {
    id: 'report-drafts',
    path: '/lab/report-drafts',
    title: 'Report drafts',
    group: 'Lab',
    audience: 'engineering'
  },
  {
    // Opens only from a link, because it needs a draft id.
    id: 'report-draft',
    path: '/lab/report-draft',
    title: 'Report draft',
    group: 'Lab',
    audience: 'engineering',
    inRail: false
  }
])
